"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setFavouriteRequestSchema = exports.sidebarViewSchema = exports.sidebarReportSchema = exports.sidebarDealSchema = exports.sidebarProjectSchema = exports.sidebarCountsSchema = void 0;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The sidebar's own read: counters, pinned projects, pinned reports.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The chrome outlives every route, so this is fetched once per workspace rather than per screen.
 * `markup.html` 71–130 is what it feeds: two pinned lists and five counters on the Workspace group,
 * plus the one on `Feladataim`.
 */
/**
 * ⚠️⚠️ **EVERY COUNT IS NULLABLE, AND `null` IS NOT `0`.** Zero is a fact about the workspace —
 * *"there are no clients"*. Null is a fact about the caller — *"you cannot see clients"*. Rendering
 * a `0` for somebody without `client.view` would state the first while meaning the second, which is
 * the same class of leak rule 5 avoids by answering `not_found` rather than `permission_denied`.
 * The sidebar draws no number at all for null.
 */
exports.sidebarCountsSchema = zod_1.z.object({
    projects: zod_1.z.number().int().nullable(),
    clients: zod_1.z.number().int().nullable(),
    people: zod_1.z.number().int().nullable(),
    /** ⚠️ OPEN tasks, not all — the mockup's own `wsTaskCount` excludes `done`. */
    tasks: zod_1.z.number().int().nullable(),
    /**
     * C24 · ACTIVE contracts, which is the mockup's own `wsContractCount`
     * (`all.filter(x => x.status === 'active').length`) rather than every row.
     *
     * ⚠️ `null` when the plan excludes contracts OR the member cannot read them — the same
     * distinction every count here makes. Zero would state that the workspace has no contracts; the
     * truth is that the question does not apply, and the row is not drawn either way.
     */
    contracts: zod_1.z.number().int().nullable(),
    /**
     * C57 · EVERY invoice, which is the mockup's own `wsInvCount` (`this.invs().length`) — not the
     * unpaid ones.
     *
     * ⚠️ **The row shipped without a counter while every other Workspace row had one**, which reads
     * as a number that failed to load rather than as a row that has none. `null` still means *the
     * question does not apply* — no contracts feature, or no permission to read one.
     */
    invoices: zod_1.z.number().int().nullable(),
    /** ⚠️ The caller's OWN saved reports — P-3 makes a workspace-wide count meaningless. */
    reports: zod_1.z.number().int().nullable(),
    worklogs: zod_1.z.number().int().nullable(),
    /** Open tasks assigned to the caller. The mockup's `myOpenCount`. */
    myOpenTasks: zod_1.z.number().int().nullable(),
    /**
     * C47 · requests waiting for THIS caller to decide. The mockup surfaces it on the nav item
     * (`wsPendingCount`), which is the whole reason it is a count rather than a screen somebody opens
     * to find out whether there is anything to do.
     *
     * ⚠️⚠️ **IT COUNTS WHAT THIS PERSON MAY DECIDE, NOT WHAT IS PENDING.** Deciding is the permission
     * OR being the requester's manager, so a manager with no permission sees their own reports'
     * requests and nobody else's. A workspace-wide pending count would send them to a queue that is
     * empty for them.
     */
    pendingApprovals: zod_1.z.number().int().nullable(),
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ EVERY REQUEST IN THE WHOLE WORKSPACE — the OTHER Kérelmek's badge.          │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **A SECOND NUMBER BECAUSE THERE ARE TWO LISTS, AND THEY COUNT DIFFERENT THINGS.**
     * `pendingApprovals` above is *what is waiting for ME* and belongs to the lead's queue; this is
     * *the workspace's requests* and belongs to the administrator's. Showing one figure on both rows
     * would put a number beside a list it does not describe — which is the mistake the badge made
     * when the global row lost its own count on 19 Sep.
     *
     * ⚠️⚠️ **EVERY STATUS, BOTH KINDS — 13 Oct 2026, the product owner.** It counted the pending
     * ones, which made the row read as a second approval queue; the workspace list is the global
     * view of everything, so its number is the size of that list like every other row in the group.
     *
     * ⚠️ `null` without `leave.view`, like every other count here: a number about a list somebody
     * may not open is a number that answers a question they cannot follow up.
     */
    requests: zod_1.z.number().int().nullable(),
    /** Phase 6 · the open deals the reader can see (DM-1's reach). `null` without `deal.view`. */
    deals: zod_1.z.number().int().nullable(),
});
exports.sidebarProjectSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    /** The identity colour, for the 8px dot. Null where the project has none. */
    color: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **EVERY TASK IN THAT PROJECT, NOT THE OPEN ONES** — and the mockup's `open_count` is
     * what it used to be. Product owner, 10 Sep 2026: *"I want all the tasks visible everywhere, not
     * just the open ones."* The complaint behind it is that one project reported three numbers to
     * one person in one sitting — 1351 imported, 819 in the rail, 1359 on the page — and only the
     * middle one was measuring something different. A rail number that disagrees with the screen it
     * opens is read as a bug however defensible it is.
     */
    tasks: zod_1.z.number().int(),
});
/** Phase 6 · a starred deal, with its open to-dos — the mockup's sidebar *Dealek* section. */
exports.sidebarDealSchema = zod_1.z.object({
    id: zod_1.z.string(),
    identifier: zod_1.z.string(),
    name: zod_1.z.string(),
    /** The client's colour, for the dot. */
    color: zod_1.z.string().nullable(),
    todos: zod_1.z.number().int(),
});
exports.sidebarReportSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
});
exports.sidebarViewSchema = zod_1.z.object({
    counts: exports.sidebarCountsSchema,
    /**
     * ⚠️⚠️ **PINNED, WHERE THE MOCKUP LISTS EVERYTHING — A DELIBERATE DIVERGENCE, `0030` ARGUES IT.**
     * `main.jsx` renders every project in the workspace and the first three saved reports in array
     * order. Both work at prototype scale and neither survives a real workspace: an unbounded sidebar
     * cannot be scanned, and "the first three inserted" is not a rule anybody can predict or change.
     * The shape is exactly as drawn; the contents became the person's own choice.
     */
    projects: zod_1.z.array(exports.sidebarProjectSchema),
    reports: zod_1.z.array(exports.sidebarReportSchema),
    deals: zod_1.z.array(exports.sidebarDealSchema),
    /**
     * ⚠️⚠️ **THE FLAG READER THE CHROME NEEDS, AND IT TRAVELS WITH THE COUNTS FOR THEIR REASON.** A
     * feature the tenant's plan excludes must not leave a navigation row behind: the server check is
     * the only security (rule 6), but a link that answers 403 is a broken application rather than a
     * plan boundary, and *"Riportok"* leading to a refusal is worse than no *"Riportok"*.
     *
     * ⚠️ **Effective values, not overrides** — `FeatureService` has already merged the tenant's
     * overrides over its plan's mapping over the registry's defaults, so the chrome does one lookup
     * and cannot reimplement the precedence differently. A key absent from this object is a key no
     * deploy registers, and the reader treats absence as off.
     */
    features: zod_1.z.record(zod_1.z.string(), zod_1.z.boolean()),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ WHAT THIS PERSON CAN DO AT ALL — the second reason a nav row is  │
     * │ absent, and it is NOT a permission the client could evaluate.         │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * `features` takes a row away when the tenant's PLAN excludes it. These take a row away when the
     * person could reach it and find nothing they are allowed to do — and both were reported in the
     * same sitting: a MEMBER seeing *Kérelmek* and opening an empty screen, and a contractor seeing
     * *Új kérelem* on a form that refuses every submission.
     *
     * ⚠️⚠️ **NEITHER IS DERIVABLE FROM THE PERMISSION SET THE CLIENT HOLDS.** Approving is
     * `leave.decide` **or being somebody's manager** (C47's rule) or being the approver named on an
     * overtime claim; requesting turns on `members.leave_managed`, which is a column and not an
     * action. A client that guessed either would guess wrong for exactly the people the rules exist
     * for, so the server answers and the chrome reads.
     *
     * ⚠️ **UI gating is UX; the endpoints still refuse** (rule 6). What this prevents is a link that
     * leads to a refusal, which reads as a broken application rather than as a boundary.
     */
    entitlements: zod_1.z.object({
        /**
         * 26 Oct 2026 · whether this person logs time at all — `members.tracks_time`, *Időt rögzít* on
         * their profile. Off, *Idő* leaves the sidebar and Home's *Időlogok* card goes with it.
         * ⚠️ Defaulting to true, like the menu rows: an older server still draws the row.
         */
        logsTime: zod_1.z.boolean().default(true),
        /** Whether this person may file a leave request at all. */
        leaveRequest: zod_1.z.boolean(),
        /** Whether the approval queue could ever hold something for them. */
        /**
         * ⚠️⚠️ **TWO ENTITLEMENTS BECAUSE THERE ARE TWO QUEUES.** `leaveApprove` was *may see some
         * queue* — true for a manager with no permission and for an administrator with every
         * permission, which is why one row had to serve both and served neither well.
         */
        leaveDecide: zod_1.z.boolean(),
        leaveViewAll: zod_1.z.boolean(),
        /**
         * 22 Oct 2026 · the workspace menu's rows, *Riportok* and the dashboards, one each — the
         * `menu.*`, `report.view` and `dashboard.*` grants, answered by the server like the two above
         * so the chrome never guesses. ⚠️ Optional on the wire, defaulting to true: a server older than
         * the client still draws every row it drew before.
         */
        menuProjects: zod_1.z.boolean().default(true),
        menuTasks: zod_1.z.boolean().default(true),
        menuContracts: zod_1.z.boolean().default(true),
        menuInvoices: zod_1.z.boolean().default(true),
        menuWorklogs: zod_1.z.boolean().default(true),
        menuPeople: zod_1.z.boolean().default(true),
        menuClients: zod_1.z.boolean().default(true),
        reports: zod_1.z.boolean().default(true),
        dashboardResource: zod_1.z.boolean().default(true),
        dashboardLogging: zod_1.z.boolean().default(true),
        dashboardUtilization: zod_1.z.boolean().default(true),
        dashboardLeave: zod_1.z.boolean().default(true),
    }),
    /**
     * F-5 · the workspace still holds demo data, so the shell draws the banner that says so — for
     * everybody, whatever they may do about it. Whether the banner's link is drawn is a permission,
     * and the screen asks that separately.
     */
    demoData: zod_1.z.boolean(),
});
/**
 * Pin or unpin.
 *
 * ⚠️ **A body with the desired STATE, not a toggle verb.** `{ favourite: true }` is idempotent — the
 * second click of a double-click lands on the same state — where a `POST /toggle` would flip twice
 * and leave the star showing the opposite of what the person meant.
 */
exports.setFavouriteRequestSchema = zod_1.z.object({
    favourite: zod_1.z.boolean(),
});
//# sourceMappingURL=sidebar.js.map